import { useEffect, useState } from "react";
import { OverlayTrigger, Popover, Tooltip } from "react-bootstrap";
import { BiChevronDown } from "react-icons/bi";
import { FiHome } from "react-icons/fi";
import { MdOutlineDiamond } from "react-icons/md";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import "./Sidebar.scss";
import upgradeIcon from "../../../images/upgrade.png";

const ROUTE_ACTIVE_ALIASES = {
  ghg_inventory: [
    "ghg_inventory",
    "add_ghg_inventory",
    "ghg_org_boundaries",
    "ghg_opr_boundaries",
    "ghg_opr_identify",
    "ghg_opr_review",
    "ghg_inventory_settings",
  ],
  "ghg/overview": ["ghg/overview", "ghg/new_overview"],
  ghg_emissions: ["ghg_emissions", "editGHGData"],
  collect_data: ["collect_data"],
  ghg_inventory_report: ["ghg_inventory_report"],
};

const getStorageKey = (menuKey) => `sidebar-menu-open-${menuKey}`;

const isRouteActive = (link, pathname) => {
  if (!link || link.startsWith("http")) {
    return false;
  }

  const aliases = ROUTE_ACTIVE_ALIASES[link] || [link];
  return aliases.some(
    (route) =>
      pathname.endsWith(`/${route}`) || pathname.includes(`/${route}/`)
  );
};

const isQueryMatch = (menuItem, search) => {
  if (!menuItem?.matchQuery) {
    return true;
  }

  const params = new URLSearchParams(search || "");
  return Object.entries(menuItem.matchQuery).every(([key, value]) => {
    const current = params.get(key);
    if (current == null || current === "") {
      return Boolean(menuItem.activeWhenNoQuery);
    }
    return current === String(value);
  });
};

const isMenuItemActive = (menuItem, pathname, search) => {
  if (!isRouteActive(menuItem?.link, pathname)) {
    return false;
  }
  return isQueryMatch(menuItem, search);
};

const hasActiveDescendant = (menuItem, pathname, search) => {
  if (!menuItem?.children?.length) {
    return isMenuItemActive(menuItem, pathname, search);
  }

  return menuItem.children.some((child) =>
    hasActiveDescendant(child, pathname, search)
  );
};

const getInitialOpenState = (item, pathname, search) => {
  if (hasActiveDescendant(item, pathname, search)) {
    return true;
  }

  if (item.persistOpen && item.menuKey) {
    const stored = localStorage.getItem(getStorageKey(item.menuKey));
    if (stored !== null) {
      return stored === "true";
    }
  }

  return false;
};

const getNavLinkTarget = (item) => {
  if (!item.search) {
    return item.link;
  }
  const search = item.search.startsWith("?") ? item.search : `?${item.search}`;
  return `${item.link}${search}`;
};

export default function SidebarItem({ item, depth = 0 }) {
  const location = useLocation();
  const [open, setOpen] = useState(() =>
    getInitialOpenState(item, location.pathname, location.search)
  );
  const navigate = useNavigate();
  const Icon = item.icon ? item.icon : FiHome;
  const hasChildren = Boolean(item.children?.length);
  const isChildActive = hasActiveDescendant(
    item,
    location.pathname,
    location.search
  );
  const isLeafActive = isMenuItemActive(
    item,
    location.pathname,
    location.search
  );

  useEffect(() => {
    if (isChildActive) {
      setOpen(true);
    }
  }, [isChildActive, location.pathname, location.search]);

  const persistOpenState = (nextOpen) => {
    setOpen(nextOpen);
    if (item.persistOpen && item.menuKey) {
      localStorage.setItem(getStorageKey(item.menuKey), String(nextOpen));
    }
  };

  const handleParentClick = (e) => {
    if (item.showDiamondIcon) {
      e.preventDefault();
      e.stopPropagation();
      if (item.onClick === "double-materiality-upsale") {
        navigate("/company_admin/double-materiality-upsale");
      } else if (item.onClick === "ghg-emissions-upsale") {
        navigate("/company_admin/ghg-emissions-upsale");
      } else {
        navigate("/company_admin/golas-kpis-upsale");
      }
      return;
    }

    e.preventDefault();
    persistOpenState(!open);
  };

  const handleParentKeyDown = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      persistOpenState(!open);
    }
  };

  const renderUpgradeIcon = () =>
    item.showDiamondIcon ? (
      <OverlayTrigger
        placement="top"
        overlay={
          <Tooltip id={`tooltip-upgrade-${item.menuKey || item.text}`}>
            This feature is not in your current plan — Upgrade to start using it
          </Tooltip>
        }
      >
        <span className="upgrade-icon">
          <img src={upgradeIcon} alt="upgrade" />
        </span>
      </OverlayTrigger>
    ) : null;

  if (hasChildren) {
    return (
      <li
        className={`sidebar-menu-item sidebar-menu-item--depth-${depth}${
          isChildActive ? " sidebar-menu-item--active-branch" : ""
        }`}
        role="none"
      >
        <button
          type="button"
          className={`sidebar-menu-toggle${isChildActive ? " active" : ""}`}
          onClick={handleParentClick}
          onKeyDown={handleParentKeyDown}
          aria-expanded={open}
          aria-haspopup="true"
          aria-label={item.text}
        >
          <div className="SB_icon">
            <Icon />
          </div>
          <span>
            {item.text}
            {renderUpgradeIcon()}
          </span>
          <div
            className={`Drop_down_Sub${open ? " Drop_down_Sub--expanded" : ""}`}
            hidden={item.showDiamondIcon}
            aria-hidden="true"
          >
            <BiChevronDown />
          </div>
        </button>
        <ul
          className={`Sub_link sidebar-submenu sidebar-submenu--depth-${
            depth + 1
          } ${open ? "show-submenu" : "hide-submenu"}`}
          role="menu"
        >
          {item.children.map((child, index) => (
            <SidebarItem
              key={child.menuKey || child.link || `${child.text}-${index}`}
              item={child}
              depth={depth + 1}
            />
          ))}
        </ul>
      </li>
    );
  }

  return (
    <li
      className={`sidebar-menu-item sidebar-menu-item--depth-${depth}`}
      role="none"
    >
      {item.disabled ? (
        <OverlayTrigger
          trigger={["hover", "focus"]}
          placement="right"
          overlay={
            <Popover id="popover-trigger-hover-focus" className="p-2">
              <div className="p-1">Coming Soon</div>
            </Popover>
          }
        >
          <span className="sidebar-menu-disabled" role="menuitem" aria-disabled="true">
            <div className="SB_icon">
              <Icon />
            </div>
            <span>
              {item.text}
              {item.showDiamondIcon && <MdOutlineDiamond />}
            </span>
          </span>
        </OverlayTrigger>
      ) : item.isExecutive ? (
        <a
          href={item.link}
          target="_blank"
          rel="noopener noreferrer"
          className="sidebar-menu-link"
          role="menuitem"
        >
          <div className="SB_icon">
            <Icon />
          </div>
          <span>
            {item.text}
            {item.showDiamondIcon && <MdOutlineDiamond />}
          </span>
        </a>
      ) : (
        <NavLink
          to={getNavLinkTarget(item)}
          className={({ isActive }) =>
            `sidebar-menu-link${
              (item.matchQuery ? isLeafActive : isActive || isLeafActive)
                ? " active"
                : ""
            }`
          }
          role="menuitem"
        >
          <div className="SB_icon">
            <Icon />
          </div>
          <span>
            {item.text}
            {item.showDiamondIcon && <MdOutlineDiamond />}
          </span>
          {item.img && <img src={item.img} alt={`${item.text} icon`} />}
        </NavLink>
      )}
    </li>
  );
}
